import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { IKnowledgeDocument } from '../../interfaces/knowledge-document.interface';
import {
  documentLabel,
  knowledgeDocumentIcon
} from '../../utils/knowledge-document-icon.util';
import { normalizeText } from '../../utils/normalize-text.util';

@Component({
  selector: 'sgt-knowledge-menu',
  templateUrl: './knowledge-menu.component.html',
  styleUrls: ['./knowledge-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class KnowledgeMenuComponent implements OnChanges {
  @Input() documents: IKnowledgeDocument[] = [];
  @Input() searchQuery = '';
  @Input() loading = false;
  @Input() truncated = false;
  @Input() limitReached = false;

  @Output() documentSelected = new EventEmitter<IKnowledgeDocument>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('listRef') listRef: ElementRef<HTMLDivElement>;

  navigableDocuments: IKnowledgeDocument[] = [];
  highlightedIndex = -1;
  hasAvailableDocuments = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.documents || changes.searchQuery) {
      this.highlightedIndex = -1;
      this.refresh();
    }
  }

  moveHighlight(direction: 1 | -1): void {
    const len = this.limitReached ? 0 : this.navigableDocuments.length;
    if (!len) {
      return;
    }
    this.highlightedIndex = (this.highlightedIndex + direction + len) % len;
    this.cdr.markForCheck();
    this.scrollToHighlighted();
  }

  selectHighlighted(): boolean {
    if (this.limitReached) {
      return false;
    }
    if (this.highlightedIndex >= 0 && this.highlightedIndex < this.navigableDocuments.length) {
      this.onDocumentClick(this.navigableDocuments[this.highlightedIndex]);
      return true;
    }
    return false;
  }

  setHighlight(index: number): void {
    this.highlightedIndex = index;
  }

  onDocumentClick(document: IKnowledgeDocument): void {
    this.documentSelected.emit(document);
  }

  label(document: IKnowledgeDocument): string {
    return documentLabel(document);
  }

  iconClass(document: IKnowledgeDocument): string {
    const { className, modifier } = knowledgeDocumentIcon(document.detectedMime);
    return `${className} knowledge-menu__icon knowledge-menu__icon--${modifier}`;
  }

  private refresh(): void {
    const available = (this.documents || []).filter(doc => doc.availableForRetrieval);
    this.hasAvailableDocuments = available.length > 0;
    this.navigableDocuments = this.filter(available);
  }

  private filter(documents: IKnowledgeDocument[]): IKnowledgeDocument[] {
    const termos = normalizeText(this.searchQuery).split(/\s+/).filter(Boolean);
    if (!termos.length) {
      return documents;
    }
    return documents.filter(doc => {
      const alvo = normalizeText(`${doc.title} ${doc.originalFilename ?? ''}`);
      return termos.every(termo => alvo.includes(termo));
    });
  }

  private scrollToHighlighted(): void {
    setTimeout(() => {
      if (!this.listRef) {
        return;
      }
      const active = this.listRef.nativeElement.querySelector(
        '.knowledge-menu__item--active'
      ) as HTMLElement;
      if (active) {
        active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }
}
